//WebChatConfig.js

import React, { useState, useEffect } from 'react';
import { Copy, Code, Palette, Globe, CheckSquare, Square, X, Save, GripVertical, MessageSquare, Asterisk, Image as ImageIcon, Layout, MousePointer, RefreshCw, Check } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import toast from 'react-hot-toast';
import api from '../../../services/api'; 

const SortableField = ({ id, label, active, required, onToggle, onToggleRequired }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : 1,
        opacity: isDragging ? 0.6 : 1,
    };

    return (
        <div ref={setNodeRef} style={style} className={`field-toggle-btn ${active ? 'active' : ''}`}>
            <div {...attributes} {...listeners} className="drag-handle">
                <GripVertical size={16} />
            </div>
            
            <div className="field-content" onClick={() => onToggle(id)}>
                {active ? <CheckSquare size={16} color="#00a884" /> : <Square size={16} />}
                <span>{label}</span>
            </div>

            {active && (
                <div 
                    className={`required-toggle ${required ? 'is-required' : ''}`} 
                    onClick={() => onToggleRequired(id)}
                    title={required ? "Campo Obrigatório" : "Campo Opcional"}
                >
                    <Asterisk size={14} />
                    <span style={{ fontSize: '10px', fontWeight: 'bold' }}>{required ? 'Obrigatório' : 'Opcional'}</span>
                </div>
            )}
        </div>
    );
};

const WebChatConfig = ({ estabelecimentoId = "4" }) => {
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [copied, setCopied] = useState(false);
    const [isChatOpen, setIsChatOpen] = useState(true);
    const [config, setConfig] = useState({
        primaryColor: "#00a884",
        title: "Atendimento Online",
        showWelcomeMsg: true,
        welcomeMsg: "Olá! Como podemos ajudar hoje?",
        buttonText: "Iniciar Chat",
        buttonShape: "rounded", // 'rounded', 'pill', 'square'
        position: "bottom-right",
        avatarUrl: "",
        fields: [
            { id: 'name', label: 'Nome Completo', active: true, required: true },
            { id: 'email', label: 'E-mail', active: false, required: false },
            { id: 'phone', label: 'WhatsApp', active: true, required: true },
            { id: 'cpf', label: 'CPF', active: false, required: false },
        ]
    });

    useEffect(() => {
        const loadConfig = async () => {
            if (!estabelecimentoId) return;
            setLoading(true);
            try {
                const response = await api.get(`cardapio/estabelecimento/${estabelecimentoId}`);
                if (response.data && response.data.webchat_config) {
                    const savedData = response.data.webchat_config;
                    const parsedConfig = typeof savedData === 'string' ? JSON.parse(savedData) : savedData;
                    setConfig(prev => ({ ...prev, ...parsedConfig }));
                }
            } catch (err) {
                if (err.response?.status !== 404) {
                    toast.error("Erro ao carregar dados do servidor.");
                }
            } finally {
                setLoading(false);
                setInitialLoading(false);
            }
        };
        loadConfig();
    }, [estabelecimentoId]);

    const handleSave = async () => {
        setLoading(true);
        try {
            await api.put(`cardapio/estabelecimento/${estabelecimentoId}`, {
                webchat_config: JSON.stringify(config),
                webchat_ativo: 1
            });
            toast.success("Configurações salvas e aplicadas com sucesso!");
        } catch (err) {
            toast.error("Erro ao salvar no banco de dados.");
        } finally {
            setLoading(false);
        }
    };

    // Handler para Upload de Imagem do Avatar
    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            toast.error("A imagem deve ter no máximo 2MB.");
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            setConfig(prev => ({ ...prev, avatarUrl: reader.result }));
            toast.success("Foto de perfil carregada com sucesso!");
        };
        reader.readAsDataURL(file);
    };

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active && over && active.id !== over.id) {
            setConfig((prev) => {
                const oldIndex = prev.fields.findIndex(f => f.id === active.id);
                const newIndex = prev.fields.findIndex(f => f.id === over.id);
                return { ...prev, fields: arrayMove(prev.fields, oldIndex, newIndex) };
            });
        }
    };

    const toggleField = (id) => {
        setConfig(prev => ({
            ...prev,
            fields: prev.fields.map(f => f.id === id ? { ...f, active: !f.active } : f)
        }));
    };

    const toggleRequired = (id) => {
        setConfig(prev => ({
            ...prev,
            fields: prev.fields.map(f => f.id === id ? { ...f, required: !f.required } : f)
        }));
    };

    // Script atualizado com todas as propriedades para injetar no widget externo
    const scriptCode = `<script>
  window.UMD_CHAT_CONFIG = {
    estabelecimentoId: "${estabelecimentoId}",
    primaryColor: "${config.primaryColor}",
    title: "${config.title}",
    showWelcome: ${config.showWelcomeMsg},
    welcomeMsg: "${config.welcomeMsg}",
    position: "${config.position || 'bottom-right'}",
    avatarUrl: "${config.avatarUrl || ''}",
    buttonText: "${config.buttonText || 'Iniciar Chat'}",
    buttonShape: "${config.buttonShape || 'rounded'}",
    fields: ${JSON.stringify(config.fields.filter(f => f.active).map(f => ({ id: f.id, label: f.label, required: f.required })), null, 2)}
  };
</script>
<script src="https://app.universomidiadigital.com.br/chat/widget.js" async></script>`;

    const handleCopyCode = () => {
        navigator.clipboard.writeText(scriptCode);
        setCopied(true);
        toast.success("Código de integração copiado com sucesso!");
        setTimeout(() => setCopied(false), 3000);
    };

    // Retorno do borderRadius com base na configuração do botão
    const getButtonRadius = () => {
        if (config.buttonShape === 'pill') return '50px';
        if (config.buttonShape === 'square') return '0px';
        return '8px';
    };

    if (initialLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', fontFamily: 'sans-serif' }}>
                <div style={{ textAlign: 'center', color: '#666' }}>
                    <RefreshCw className="spin" size={32} style={{ marginBottom: '10px', color: '#00a884' }} />
                    <p>Carregando configurações do Webchat...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="wa-row-premium" style={{ display: 'flex', gap: '20px', padding: '20px', maxWidth: '1300px', margin: '0 auto', fontFamily: 'sans-serif' }}>
            
            <div className="wa-card-premium" style={{ flex: 1, background: '#fff', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                <div style={{ padding: '20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafafa' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Globe size={22} color="#00a884" />
                        <h3 style={{ margin: 0, fontSize: '18px', color: '#333' }}>Configurar Webchat</h3>
                    </div>
                    <button onClick={handleSave} className="wa-btn-save" disabled={loading}>
                        <Save size={16} /> {loading ? 'Salvando...' : 'Salvar Alterações'}
                    </button>
                </div>

                <div style={{ padding: '20px' }}>
                    <div className="modal-section">
                        <label className="section-label"><Palette size={14} /> Visual e Título</label>
                        <div style={{ display: 'flex', gap: '15px', marginTop: '12px' }}>
                            <input type="color" value={config.primaryColor} onChange={e => setConfig({...config, primaryColor: e.target.value})} style={{ width: '60px', height: '40px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }} />
                            <input type="text" className="wa-input-text" value={config.title} onChange={e => setConfig({...config, title: e.target.value})} placeholder="Título do Chat" />
                        </div>
                    </div>

                    {/* Layout, Posição e Upload de Avatar */}
                    <div className="modal-section" style={{ marginTop: '25px' }}>
                        <label className="section-label"><Layout size={14} /> Posição e Foto de Perfil</label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '12px' }}>
                            <div>
                                <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '5px' }}>Posição na Tela</label>
                                <select 
                                    className="wa-input-text" 
                                    value={config.position || 'bottom-right'} 
                                    onChange={e => setConfig({...config, position: e.target.value})}
                                    style={{ marginTop: 0 }}
                                >
                                    <option value="bottom-right">Inferior Direito</option>
                                    <option value="bottom-left">Inferior Esquerdo</option>
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '5px' }}>Upload do Avatar / Foto</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <label className="wa-btn-upload">
                                        <ImageIcon size={16} /> Escolher Imagem
                                        <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
                                    </label>
                                    {config.avatarUrl && (
                                        <button 
                                            onClick={() => setConfig(prev => ({ ...prev, avatarUrl: '' }))} 
                                            style={{ background: '#ff4d4f', color: '#fff', border: 'none', padding: '8px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
                                        >
                                            Remover
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Configuração do Botão do Formulário */}
                    <div className="modal-section" style={{ marginTop: '25px' }}>
                        <label className="section-label"><MousePointer size={14} /> Aparência do Botão</label>
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '15px', marginTop: '12px' }}>
                            <div>
                                <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '5px' }}>Texto do Botão</label>
                                <input 
                                    type="text" 
                                    className="wa-input-text" 
                                    value={config.buttonText} 
                                    onChange={e => setConfig({...config, buttonText: e.target.value})} 
                                    style={{ marginTop: 0 }}
                                />
                            </div>
                            <div>
                                <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '5px' }}>Formato / Estilo</label>
                                <select 
                                    className="wa-input-text" 
                                    value={config.buttonShape || 'rounded'} 
                                    onChange={e => setConfig({...config, buttonShape: e.target.value})}
                                    style={{ marginTop: 0 }}
                                >
                                    <option value="rounded">Arredondado</option>
                                    <option value="pill">Pílula (Total)</option>
                                    <option value="square">Quadrado</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="modal-section" style={{ marginTop: '25px', padding: '15px', background: '#f9f9f9', borderRadius: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <label className="section-label"><MessageSquare size={14} /> Boas-vindas</label>
                            <div onClick={() => setConfig({...config, showWelcomeMsg: !config.showWelcomeMsg})} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: config.showWelcomeMsg ? '#00a884' : '#999' }}>
                                {config.showWelcomeMsg ? <CheckSquare size={18} /> : <Square size={18} />} {config.showWelcomeMsg ? 'Ativada' : 'Desativada'}
                            </div>
                        </div>
                        {config.showWelcomeMsg && <input type="text" className="wa-input-text" style={{ marginTop: '12px', background: '#fff' }} value={config.welcomeMsg} onChange={e => setConfig({...config, welcomeMsg: e.target.value})} />}
                    </div>

                    <div className="modal-section" style={{ marginTop: '25px' }}>
                        <label className="section-label"><CheckSquare size={14} /> Campos do Formulário</label>
                        <p style={{ fontSize: '12px', color: '#777', margin: '5px 0 15px' }}>Ative os campos e defina quais são obrigatórios.</p>
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={config.fields.map(f => f.id)} strategy={verticalListSortingStrategy}>
                                {config.fields.map(field => (
                                    <SortableField 
                                        key={field.id} 
                                        id={field.id} 
                                        label={field.label} 
                                        active={field.active} 
                                        required={field.required}
                                        onToggle={toggleField} 
                                        onToggleRequired={toggleRequired}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>
                    </div>

                    {/* SEÇÃO DE INTEGRAÇÃO APRIMORADA */}
                    <div className="integration-section" style={{ marginTop: '30px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '18px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Code size={18} color="#0066ff" />
                                <span style={{ fontWeight: '700', fontSize: '14px', color: '#1e293b' }}>Código de Integração do Widget</span>
                            </div>
                            <span style={{ fontSize: '11px', background: '#e0f2fe', color: '#0369a1', padding: '3px 8px', borderRadius: '4px', fontWeight: '500' }}>
                                Instalação via Script
                            </span>
                        </div>
                        <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 12px 0', lineHeight: '1.5' }}>
                            Copie o código abaixo e cole-o no HTML do seu site, logo antes da tag de fechamento <code>&lt;/body&gt;</code>. O widget será carregado de forma assíncrona automaticamente.
                        </p>
                        <div style={{ position: 'relative' }}>
                            <pre className="wa-code-block">{scriptCode}</pre>
                            <button 
                                onClick={handleCopyCode} 
                                className={`wa-btn-copy-enhanced ${copied ? 'copied' : ''}`}
                            >
                                {copied ? <Check size={14} /> : <Copy size={14} />} 
                                {copied ? 'Copiado!' : 'Copiar Código'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Painel de Preview em Tempo Real com Botão Flutuante Interativo */}
            <div style={{ width: '380px', position: 'sticky', top: '20px', height: 'fit-content' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#444', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>Preview em Tempo Real</span>
                    <span style={{ fontSize: '11px', fontWeight: 'normal', color: '#666' }}>Clique no botão abaixo</span>
                </div>
                
                <div style={{ 
                    position: 'relative', 
                    height: '540px', 
                    background: '#f1f5f9', 
                    borderRadius: '12px', 
                    border: '1px solid #cbd5e1', 
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.08)'
                }}>
                    {/* Barra superior de navegador simulado */}
                    <div style={{ background: '#e2e8f0', padding: '10px 15px', display: 'flex', alignItems: 'center', gap: '6px', borderBottom: '1px solid #cbd5e1' }}>
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#ef4444' }}></div>
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#f59e0b' }}></div>
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#10b981' }}></div>
                        <div style={{ flex: 1, background: '#fff', borderRadius: '4px', padding: '2px 10px', fontSize: '10px', color: '#64748b', marginLeft: '10px', textAlign: 'center' }}>
                            seusite.com.br
                        </div>
                    </div>

                    {/* Conteúdo fictício do site ao fundo */}
                    <div style={{ padding: '20px', flex: 1, opacity: 0.35, display: 'flex', flexDirection: 'column', gap: '12px', pointerEvents: 'none' }}>
                        <div style={{ height: '22px', background: '#cbd5e1', borderRadius: '4px', width: '70%' }}></div>
                        <div style={{ height: '10px', background: '#cbd5e1', borderRadius: '4px', width: '100%' }}></div>
                        <div style={{ height: '10px', background: '#cbd5e1', borderRadius: '4px', width: '85%' }}></div>
                        <div style={{ height: '140px', background: '#cbd5e1', borderRadius: '8px', width: '100%', marginTop: '10px' }}></div>
                        <div style={{ height: '10px', background: '#cbd5e1', borderRadius: '4px', width: '90%' }}></div>
                    </div>

                    {/* Janela de Chat Aberta (Condicional) */}
                    {isChatOpen && (
                        <div style={{
                            position: 'absolute',
                            bottom: '75px',
                            [config.position === 'bottom-left' ? 'left' : 'right']: '15px',
                            width: '325px',
                            background: '#fff',
                            borderRadius: '12px',
                            boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
                            border: '1px solid #e2e8f0',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            zIndex: 10
                        }}>
                            <div className="preview-header" style={{ backgroundColor: config.primaryColor, padding: '12px 15px', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ width: '28px', height: '28px', borderRadius: '50%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)' }}>
                                        {config.avatarUrl ? (
                                            <img src={config.avatarUrl} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : (
                                            <Globe size={16} />
                                        )}
                                    </div>
                                    <strong style={{ fontSize: '13px' }}>{config.title}</strong>
                                </div>
                                <X size={16} style={{ cursor: 'pointer' }} onClick={() => setIsChatOpen(false)} />
                            </div>
                            <div style={{ padding: '12px', background: '#f8fafc', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {config.showWelcomeMsg && (
                                    <div style={{ background: '#fff', padding: '10px 12px', borderRadius: '0 10px 10px 10px', fontSize: '11px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', color: '#334155' }}>
                                        {config.welcomeMsg}
                                    </div>
                                )}
                                <div style={{ background: '#fff', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                    {config.fields.filter(f => f.active).map(f => (
                                        <div key={f.id} style={{ border: '1px solid #e2e8f0', padding: '6px 8px', borderRadius: '4px', marginBottom: '6px', fontSize: '10px', color: '#64748b', background: '#f8fafc' }}>
                                            {f.label} {f.required && <span style={{ color: '#ef4444' }}>*</span>}
                                        </div>
                                    ))}
                                    <button style={{ width: '100%', backgroundColor: config.primaryColor, color: 'white', border: 'none', padding: '8px', borderRadius: getButtonRadius(), fontWeight: 'bold', fontSize: '11px', cursor: 'pointer', marginTop: '4px' }}>
                                        {config.buttonText || "Iniciar Chat"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Botão Flutuante do Chat (Launcher) */}
                    <div style={{
                        position: 'absolute',
                        bottom: '15px',
                        [config.position === 'bottom-left' ? 'left' : 'right']: '15px',
                        zIndex: 11
                    }}>
                        <button
                            onClick={() => setIsChatOpen(!isChatOpen)}
                            style={{
                                backgroundColor: config.primaryColor,
                                color: '#fff',
                                border: 'none',
                                padding: '10px 16px',
                                borderRadius: getButtonRadius(),
                                boxShadow: '0 4px 15px rgba(0,0,0,0.25)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                fontWeight: '600',
                                fontSize: '12px',
                                transition: 'all 0.2s'
                            }}
                            title="Abrir/Fechar Chat"
                        >
                            {isChatOpen ? <X size={16} /> : <MessageSquare size={16} />}
                            <span>{config.buttonText || "Iniciar Chat"}</span>
                        </button>
                    </div>
                </div>
            </div>

            <style>{`
                .section-label { display: flex; align-items: center; gap: 8px; font-weight: 700; font-size: 13px; color: #444; text-transform: uppercase; }
                .wa-input-text { width: 100%; padding: 10px; border-radius: 6px; border: 1px solid #ddd; font-size: 14px; margin-top: 5px; box-sizing: border-box; }
                .wa-btn-save { background: #00a884; color: white; border: none; padding: 8px 16px; border-radius: 6px; cursor: pointer; display: flex; align-items: center; gap: 8px; font-weight: 600; }
                .wa-btn-upload { background: #f0f2f5; color: #333; border: 1px solid #ddd; padding: 9px 14px; border-radius: 6px; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 500; }
                .wa-btn-upload:hover { background: #e4e6eb; }
                
                .field-toggle-btn { display: flex; align-items: center; background: #fff; border: 1px solid #ddd; border-radius: 8px; margin-bottom: 8px; transition: all 0.2s; }
                .field-toggle-btn.active { border-color: #00a884; background: #f0fff9; }
                .field-content { padding: 12px; display: flex; align-items: center; gap: 10px; flex: 1; cursor: pointer; font-size: 14px; }
                .drag-handle { padding: 12px; cursor: grab; color: #ccc; border-right: 1px solid #eee; }
                
                .required-toggle { display: flex; align-items: center; gap: 4px; padding: 6px 10px; cursor: pointer; color: #ccc; border-left: 1px solid #eee; transition: 0.2s; }
                .required-toggle.is-required { color: #ff4d4f; background: #fff1f0; }
                .required-toggle:hover { background: #f5f5f5; }

                .wa-code-block { background: #0f172a; color: #e2e8f0; padding: 15px; border-radius: 8px; font-size: 11px; overflow-x: auto; white-space: pre-wrap; font-family: monospace; border: 1px solid #1e293b; margin: 0; }
                
                .wa-btn-copy-enhanced { 
                    position: absolute; 
                    top: 10px; 
                    right: 10px; 
                    background: #334155; 
                    border: none; 
                    color: #fff; 
                    padding: 6px 12px; 
                    border-radius: 6px; 
                    cursor: pointer; 
                    font-size: 12px; 
                    font-weight: 500;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    transition: background 0.2s;
                }
                .wa-btn-copy-enhanced:hover { background: #475569; }
                .wa-btn-copy-enhanced.copied { background: #059669; }

                .spin { animation: spin 1s linear infinite; }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
};

export default WebChatConfig;